import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth, useData, useNotifs } from '../contexts/index';
import {
  latestPendingAgreementRequest,
  type StaffAgreementRequestSummary,
} from '../core/models/StaffAgreementChangeRequest';
import { ROLES } from '../core/constants/roles';
import { supportsStaffAgreements } from '../core/rules/staffAgreements';
import { useTranslation } from '../i18n/index';
import { usePresentAppError } from './usePresentAppError';

const EMPTY_SUMMARY: StaffAgreementRequestSummary = {
  requests: [],
  quarterKey: '',
  used: 0,
  remaining: 2,
};

function newClientRequestId(): string {
  return globalThis.crypto?.randomUUID?.()
    ?? `agreement-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/** Bankers may list another user's requests; lister/runner may list their own. */
function canListAgreementRequests(
  viewerRole: string | undefined,
  targetRole: string | undefined,
  viewerId: string | undefined,
  targetId: string,
): boolean {
  if (!targetId) return false;
  if (viewerRole === ROLES.BANKER) return supportsStaffAgreements(targetRole);
  return viewerId === targetId && supportsStaffAgreements(targetRole);
}

type AgreementTargetUser = {
  id?: string;
  role?: string;
  houseId?: string;
  name?: string;
};

export function useStaffAgreementRequests(targetUser?: AgreementTargetUser | null) {
  const { adapter } = useData();
  const { user } = useAuth();
  const { push } = useNotifs();
  const t = useTranslation();
  const presentAppError = usePresentAppError();
  const target = targetUser ?? user;
  const requesterId = String(target?.id ?? '');
  const [summary, setSummary] = useState<StaffAgreementRequestSummary>(EMPTY_SUMMARY);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const toUserMessage = useCallback((err: unknown) => {
    const { copy } = presentAppError(err, { t });
    return copy?.title || copy?.hint || t.err_generic || 'Something went wrong.';
  }, [presentAppError, t]);

  const reload = useCallback(async () => {
    if (
      !requesterId
      || !adapter.listAgreementRequests
      || !canListAgreementRequests(
        user?.role,
        String(target?.role ?? ''),
        user?.id,
        requesterId,
      )
    ) {
      setSummary(EMPTY_SUMMARY);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      setSummary(await adapter.listAgreementRequests(requesterId));
    } catch (err) {
      setError(toUserMessage(err));
    } finally {
      setLoading(false);
    }
  }, [adapter, requesterId, target?.role, user?.id, user?.role, toUserMessage]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const createRequest = useCallback(async (reason: string) => {
    if (!adapter.createAgreementRequest || !target) throw new Error('Agreement requests unavailable.');
    setLoading(true);
    setError('');
    try {
      await adapter.createAgreementRequest({
        reason,
        clientRequestId: newClientRequestId(),
        requester: {
          id: target.id,
          role: target.role,
          houseId: target.houseId,
          name: target.name,
        },
      });
      await reload();
    } catch (err) {
      setError(toUserMessage(err));
      throw err;
    } finally {
      setLoading(false);
    }
  }, [adapter, target, reload, toUserMessage]);

  const reviewRequest = useCallback(async (
    requestId: string,
    decision: 'approved' | 'rejected',
    commission?: number,
    reviewerNote?: string,
  ) => {
    if (!adapter.reviewAgreementRequest) throw new Error('Agreement review unavailable.');
    setLoading(true);
    setError('');
    try {
      await adapter.reviewAgreementRequest({
        houseId: String(target?.houseId ?? ''),
        requestId,
        decision,
        commission,
        reviewerNote,
      });
      await reload();
      push({
        cat: 'users',
        sev: decision === 'approved' ? 'success' : 'warn',
        title: decision === 'approved'
          ? t.agreementRequestApproved
          : t.agreementRequestRejected,
        body: String(target?.name ?? ''),
      });
    } catch (err) {
      setError(toUserMessage(err));
      throw err;
    } finally {
      setLoading(false);
    }
  }, [adapter, target, reload, toUserMessage, push, t]);

  const pendingRequest = useMemo(
    () => latestPendingAgreementRequest(summary.requests),
    [summary.requests],
  );

  return {
    ...summary,
    pendingRequest,
    loading,
    error,
    reload,
    createRequest,
    reviewRequest,
  };
}
